import { useCallback, useEffect, useState } from 'react';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import {
  BreadcrumbComponent,
  Button,
  CaretDownIcon,
  ModalComponent,
} from '../../components/shared';
import { useWidth } from '../../lib/useWidth';
import { useDispatch, useSelector } from 'react-redux';
import { loadInventories } from '../../store/inventories/inventories.actions';
import { ColumnsType } from 'antd/lib/table';
import { sendSingleRequest } from '../../apis';
import InventoryModal from './InventoryModal';
import useMenuHandler from '../../lib/useMenuHandler';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import { formatNumber } from '../../entities/util-functions';
import { selectInventories } from '../../store/inventories/inventories.selector';
import { IInventoryResource } from '../../entities/inventory.entities';
import { IFarmResource } from '../../entities/farms.entities';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { TLang } from '../../entities/ui.entities';
import DotsMenu from '../../components/shared/dropdown-menu/DotsMenu';
import { defaultDateFormat, displayValueUnit } from '../../lib/common.helpers';
import { selectAccount, selectIsAdmin } from '../../store/auth/auth.selector';

interface IMusselRow extends IInventoryResource {
  performance?: number | null;
}

const basicColumns = (lang: TLang | undefined, unitType: any) => [
  {
    title: 'ID',
    key: 'id',
    render: (x: IInventoryResource) => <span>{x.id}</span>,
  },
  {
    title: translate(lang, 'Type'),
    key: 'type',
    render: (x: IInventoryResource) => <span>{x.type}</span>,
  },
  {
    title: translate(lang, 'Name'),
    key: 'name',
    render: (x: IInventoryResource) => <span>{x.name}</span>,
  },
  {
    title: translate(lang, 'Quantity'),
    key: 'quantity',
    render: (x: IInventoryResource) => <span>{x.quantity}</span>,
  },
  {
    title: translate(lang, 'Available Quantity'),
    key: 'available_quantity',
    render: (x: IInventoryResource) => <span>{x.available_quantity}</span>,
  },
  {
    title: translate(lang, 'Price'),
    key: 'price',
    render: (x: IInventoryResource) => <span>{`$ ${x.price}`}</span>,
  },
  {
    title: translate(lang, 'Performance'),
    key: 'performance',
    render: (x: IMusselRow) => (
      <span>
        {x.performance
          ? `1 float - ${formatNumber(
              x.performance < 1000 ? x.performance : x.performance / 1000,
            )} ${x.performance < 1000 ? 'kg' : 'tons'}`
          : '-'}
      </span>
    ),
  },
  {
    title: translate(lang, 'Level'),
    key: 'level',
    render: (x: IMusselRow) => (
      <span>{x.level === 1 ? 'Surface' : 'Submerged'}</span>
    ),
  },
  {
    title: translate(lang, 'Volume'),
    key: 'volume',
    render: (x: IInventoryResource) => (
      <span>{displayValueUnit(x.volume, unitType, 'volume_m3', '-')}</span>
    ),
  },
];

interface ISubRow {
  farm_id: number;
  line_id: number;
  farm_number: string;
  line_name: string;
  current_stock: number;
  last_float_job: string;
  last_assessment_date: string | null;
}

const getSubRows = (
  inventory: IInventoryResource,
  farms: IFarmResource[],
): ISubRow[] => {
  let result: ISubRow[] = [];

  for (let farm of farms) {
    for (let line of farm.lines) {
      if (!line.growing_cycle) continue;

      const found = line.growing_cycle.inventories_sum.find(
        x => x.inventory_id === inventory.id,
      );
      if (!found) continue;

      const tmp = inventory.inventory_seeds.filter(x => x.line_id === line.id);
      if (tmp.length <= 0) continue;

      tmp.sort((a, b) => (a.manage_date > b.manage_date ? -1 : 1));
      const lastItem = tmp[0];

      result.push({
        farm_id: farm.id,
        line_id: line.id,
        farm_number: farm.farm_number,
        line_name: line.line_name,
        current_stock: found.quantity,
        last_float_job: defaultDateFormat(lastItem.manage_date) ?? '-',
        last_assessment_date: defaultDateFormat(
          line.growing_cycle.last_assessment?.assessment_date,
          '-',
        ),
      });
    }
  }

  return result;
};

const subColumns = (lang: TLang | undefined) => [
  {
    title: translate(lang, 'Farm Number'),
    key: 'farm_number',
    render: (x: ISubRow) => <span>{x.farm_number}</span>,
  },
  {
    title: translate(lang, 'Line name'),
    key: 'line_name',
    render: (x: ISubRow) => <span>{x.line_name}</span>,
  },
  {
    title: translate(lang, 'Current stock'),
    key: 'current_stock',
    render: (x: ISubRow) => <span>{x.current_stock}</span>,
  },
  {
    title: translate(lang, '_last_float_job_date'),
    key: 'last_float_job',
    render: (x: ISubRow) => <span>{x.last_float_job}</span>,
  },
  {
    title: translate(lang, 'Last assessment date'),
    key: 'last_assessment_date',
    render: (x: ISubRow) => <span>{x.last_assessment_date}</span>,
  },
  {
    title: '',
    key: 'right',
  },
];

const InventoriesPage = () => {
  const width = useWidth();
  const dispatch = useDispatch();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const inventories = useSelector(selectInventories);
  const unitType = useSelector(selectAccount)?.display_unit;
  const isAdmin = useSelector(selectIsAdmin);

  const { redirectToLine } = useMenuHandler();
  const breadcrumbItems = getBreadcrumbMenu('INVENTORIES', lang);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [deleteShow, setDeleteShow] = useState(false);
  const [disable, setDisable] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [performs, setPerforms] =
    useState<Array<{ id: number; performance: number | null }>>();

  const loadData = useCallback(async () => {
    await dispatch(loadInventories());
    const res = await sendSingleRequest(
      {},
      'GET',
      'api/inventory/performances',
      true,
    );
    if (res.status) {
      setPerforms(res.data);
    }
  }, [dispatch]);

  const showAddModal = () => {
    setSelectedItem(null);
    setModalTitle('Add new inventory');
    setShowModal(true);
  };
  const showEditModal = (evt: any, item: any) => {
    setModalTitle('Edit inventory');
    setSelectedItem(item);
    setShowModal(true);
  };
  const showDeleteModal = (evt: any, item: any) => {
    setSelectedItem(item);
    setDeleteShow(true);
  };
  const confirmSubmit = () => {
    setDisable(false);
    setShowModal(false);
  };
  const deleteSelectedItem = async () => {
    if (!selectedItem) return;
    setDisable(true);
    const res = await sendSingleRequest(
      {},
      'DELETE',
      `api/inventory/inventories/${selectedItem.id}`,
      true,
    );
    if (res.status) {
      setDeleteShow(false);
      setDisable(false);
      loadData();
    } else {
      alert(res.data?.message ?? 'Server error');
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tableColumns = [
    ...basicColumns(lang, unitType),
    {
      key: 'more',
      title: 'More',
      align: 'right',
      render: (x: IInventoryResource) => (
        <DotsMenu
          items={[
            {
              label: translate(lang, 'Edit'),
              onClick: evt => showEditModal(evt, x),
            },
            {
              label: translate(lang, 'Delete'),
              onClick: evt => showDeleteModal(evt, x),
              disabled: !isAdmin,
            },
          ]}
        />
      ),
    },
  ] as ColumnsType<any>;

  const rowsData = inventories.map(x => {
    const t = performs?.find(y => y.id === x.id);
    return { ...x, performance: t === undefined ? null : t.performance };
  });

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative farms__line'>
        {width > 768 ? (
          <div className='d-flex justify-between align-center pl-21 pr-15 pt-28 pb-28'>
            <BreadcrumbComponent items={breadcrumbItems} />
            <Link to='#' onClick={showAddModal}>
              <Button color='blue' size={1} width='middle' type='fill'>
                {translate(lang, 'Add Inventory')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className='w-100'>
            <Button
              className='w-100 mt-17'
              color='blue'
              size={1}
              width='middle'
              type='fill'
              onClick={showAddModal}
            >
              {translate(lang, 'Add Inventory')}
            </Button>
          </div>
        )}
        <div className='farms__line-content'>
          <div className='w-100 pos-relative overflow-auto'>
            <Table
              rowKey='id'
              className='table table--isFarm'
              style={{
                width: '1200px',
                minWidth: '100%',
                maxWidth: '1200px',
              }}
              pagination={false}
              columns={tableColumns}
              dataSource={rowsData as any}
              expandable={{
                expandedRowRender: d => {
                  return (
                    <Table
                      rowKey='line_id'
                      className='table is__child'
                      columns={subColumns(lang)}
                      pagination={false}
                      dataSource={getSubRows(d, farmsData)}
                      onRow={dR => ({
                        onClick: () => redirectToLine(dR.farm_id, dR.line_id),
                      })}
                    />
                  );
                },
                expandIcon: ({ onExpand, record }) => (
                  <div
                    className='pt-20 pb-20'
                    onKeyDown={() => undefined}
                    onClick={event => {
                      event.stopPropagation();
                      onExpand(record, event);
                    }}
                  >
                    <CaretDownIcon />
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>
      {showModal && (
        <InventoryModal
          visible={true}
          title={translate(lang, modalTitle)}
          onCancel={() => setShowModal(false)}
          onConfirm={confirmSubmit}
          data={selectedItem}
          updateId={selectedItem?.id ?? undefined}
        />
      )}
      <ModalComponent
        visible={deleteShow}
        onCancel={() => setDeleteShow(false)}
        type='delete'
        title={translate(lang, 'Delete')}
        text={translate(lang, '_delete_data')}
        disabled={disable}
        onConfirm={deleteSelectedItem}
      />
    </div>
  );
};

export default InventoriesPage;
